import { createContext, useContext } from 'react';
import type { Tenant, TenantInvitation, TenantRole } from '@peruselab/api-client/tenants';

/** #1051 -- the tenant the app acts for, the user's organisations and their pending invitations. */
export interface ActiveTenantValue {
  /** The organisations the user belongs to (the personal tenant is implicit). */
  readonly tenants: readonly Tenant[];
  /** The active organisation, or `null` for the personal tenant. */
  readonly active: Tenant | null;
  /** The user's role in the active tenant: always Administrator of their personal tenant. */
  readonly role: TenantRole;
  /** Changes in the active tenant: true for Editors and Administrators. */
  readonly canEdit: boolean;
  /** Changes when the user or the active tenant changes; keys what belongs to the tenant. */
  readonly scopeKey: string;
  readonly invitations: readonly TenantInvitation[];
  /**
   * The user's role in a given tenant: `undefined` is the active one, `null` the personal one (always
   * Administrator). An organisation they no longer belong to reads as Viewer -- nothing to change.
   */
  roleIn(tenantId: string | null | undefined): TenantRole;
  /** A tenant's name for the UI: `null` is the personal tenant. */
  nameOf(tenantId: string | null): string | null;
  switchTo(tenantId: string | null): void;
  respond(invitation: TenantInvitation, accept: boolean): Promise<void>;
  /** Reloads the organisations and invitations (after creating, joining or leaving one). */
  refresh(): Promise<void>;
}

const PERSONAL: ActiveTenantValue = {
  tenants: [],
  active: null,
  role: 'ADMINISTRATOR',
  canEdit: true,
  scopeKey: 'anonymous:personal',
  invitations: [],
  roleIn: () => 'ADMINISTRATOR',
  nameOf: () => null,
  switchTo: () => {},
  respond: async () => {},
  refresh: async () => {}
};

export const ActiveTenantContext = createContext<ActiveTenantValue>(PERSONAL);

/** Outside the provider (signed out, tests), everything is the personal tenant. */
export function useActiveTenant(): ActiveTenantValue {
  return useContext(ActiveTenantContext);
}

/**
 * #1064 (ADR 0019 D7): whether the active space lets its structures go to outside services --
 * PubChem lookups and image recognition. A person's own space always does; an organisation decides.
 */
export function useOutsideLookupsAllowed(): boolean {
  return useActiveTenant().active?.outsideLookups !== false;
}
